/** @jsxImportSource @/lib/r3-runtime */
/* eslint-disable react/no-unknown-property */
import React, { Suspense, useMemo, useEffect } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Grid, Environment, Sky, Stars } from "@react-three/drei";
import * as THREE from "three";
import { useEditor } from "@/lib/store";
import { objectFootprint } from "@/lib/objectDefs";

// Conversion: 1 unit in scene = 1 metre. Floor coordinates in mm.
const MM = 0.001;

function Floor({ floor }) {
  const verts = useMemo(() => floor.vertices || [], [floor.vertices]);
  const wallFinish  = floor.wall_finish  || "plain";
  const wallColor   = floor.wall_color   || "#e7dfd2";
  const floorFinish = floor.floor_finish || "concrete";
  const floorColor  = floor.floor_color  || "#c8c2b6";
  // Compute centroid for placement; vertices already in mm
  //
  // NOTE ON COORDINATE FLIP:
  // Walls, objects and obstacles all map polygon.y → world +Z directly.
  // The floor mesh uses `rotation.x = -π/2` to lay a 2D Shape flat, which
  // has the side-effect of mapping the Shape's local Y → world -Z (mirror
  // across the Z-axis). To keep the floor mesh aligned with the walls we
  // NEGATE Y when building the Shape below — the final world coordinates
  // then match everything else in the scene.
  const shape = useMemo(() => {
    if (verts.length < 3) return null;
    const sh = new THREE.Shape();
    verts.forEach((v, i) => {
      const x = v.x * MM;
      const y = -v.y * MM;   // ← flip to compensate for the mesh's X rotation
      if (i === 0) sh.moveTo(x, y);
      else sh.lineTo(x, y);
    });
    sh.closePath();
    return sh;
  }, [verts]);

  const wallsGeom = useMemo(() => {
    if (verts.length < 2) return { walls: [], doors: [] };
    const h = (floor.wall_height || 6000) * MM;
    const thickness = 0.12;
    const doors = floor.doors || [];
    // Centroid used to compute outward normal for each edge — this keeps the
    // wall's INTERIOR face flush with the floor-polygon edge, so no strip of
    // interior floor peeks out past the wall (which shows as a bright "halo"
    // in night mode).
    const cxAll = verts.reduce((s, v) => s + v.x * MM, 0) / verts.length;
    const cyAll = verts.reduce((s, v) => s + v.y * MM, 0) / verts.length;
    const walls = [];
    const doorMeshes = [];
    for (let i = 0; i < verts.length; i++) {
      const a = verts[i];
      const b = verts[(i + 1) % verts.length];
      const ax = a.x * MM, ay = a.y * MM;
      const bx = b.x * MM, by = b.y * MM;
      const totalLen = Math.hypot(bx - ax, by - ay);
      if (totalLen < 0.01) continue;
      const ux = (bx - ax) / totalLen, uy = (by - ay) / totalLen;
      const angle = Math.atan2(by - ay, bx - ax);
      // Outward normal — points AWAY from centroid.
      let nx = -uy, ny = ux;
      const mxAll = (ax + bx) / 2, myAll = (ay + by) / 2;
      if ((mxAll - cxAll) * nx + (myAll - cyAll) * ny < 0) { nx = -nx; ny = -ny; }

      // Collect doors on this edge, sorted by offset. Clamp to edge bounds.
      const edgeDoors = doors
        .filter((d) => d.edge_idx === i)
        .map((d) => {
          const w = Math.max(0.1, (d.width || 1200) * MM);
          const off = Math.max(0, Math.min(totalLen - w, (d.offset || 0) * MM));
          return { ...d, wM: w, hM: Math.max(0.1, (d.height || 2100) * MM), offM: off };
        })
        .sort((x, y) => x.offM - y.offM);

      // Break the edge into wall segments AND a top-header segment above each door.
      const segments = [];
      let cur = 0;
      for (const d of edgeDoors) {
        if (d.offM > cur) segments.push([cur, d.offM, "full"]);
        // Header (lintel) above the door — only if door is shorter than wall
        if (d.hM < h - 0.02) segments.push([d.offM, d.offM + d.wM, "header", d.hM]);
        cur = Math.max(cur, d.offM + d.wM);
      }
      if (cur < totalLen) segments.push([cur, totalLen, "full"]);

      for (const seg of segments) {
        const [s, e, kind, doorH] = seg;
        const segLen = e - s;
        if (segLen < 0.001) continue;
        const midS = s + segLen / 2;
        const cx0 = ax + ux * midS;
        const cy0 = ay + uy * midS;
        const cx = cx0 + nx * (thickness / 2);
        const cy = cy0 + ny * (thickness / 2);
        if (kind === "full") {
          walls.push({ cx, cy, len: segLen, angle, h, thickness, cy0y: h / 2 });
        } else {
          // Header (above-door lintel). Positioned above door height, height = h - doorH.
          const headerH = h - doorH;
          walls.push({
            cx, cy, len: segLen, angle, h: headerH, thickness,
            cy0y: doorH + headerH / 2,
          });
        }
      }

      // Emit door frame meshes for 3D rendering
      for (const d of edgeDoors) {
        const midS = d.offM + d.wM / 2;
        const cx0 = ax + ux * midS;
        const cy0 = ay + uy * midS;
        const cx = cx0 + nx * (thickness / 2);
        const cy = cy0 + ny * (thickness / 2);
        doorMeshes.push({
          id: d.id, kind: d.kind, cx, cy, angle,
          len: d.wM, h: d.hM, thickness,
        });
      }
    }
    return { walls, doors: doorMeshes };
  }, [verts, floor.wall_height, floor.doors]);

  // Bounding box of the floor polygon (metres) for texture scaling.
  const fxs = verts.map((v) => v.x * MM), fys = verts.map((v) => v.y * MM);
  const fW = fxs.length ? Math.max(...fxs) - Math.min(...fxs) : 10;
  const fH = fys.length ? Math.max(...fys) - Math.min(...fys) : 10;
  const floorMat = useMaterialFinish(floorFinish, floorColor, [fW, fH]);
  const wallH_m = (floor.wall_height || 6000) * MM;
  const wallMat = useMaterialFinish(wallFinish, wallColor, [10, wallH_m]);

  if (!shape) return null;

  return (
    <group>
      {/* Warehouse floor — colour + optional finish texture */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <shapeGeometry args={[shape]} />
        <meshStandardMaterial
          color={floorMat.color}
          map={floorMat.map}
          roughness={floorMat.roughness}
          metalness={floorFinish === "epoxy" ? 0.25 : 0.05}
        />
      </mesh>
      {/* Walls — segmented around door openings. `castShadow` OFF so the walls
          don't throw a giant rectangle onto the outdoor ground. Tagged with
          userData.isWall so the SnapshotBridge can hide them during
          perspective PDF exports (walls otherwise obscure the interior). */}
      {(wallsGeom.walls || []).map((w, i) => (
        <mesh
          key={`w-${i}`}
          position={[w.cx, w.cy0y, w.cy]}
          rotation={[0, -w.angle, 0]}
          receiveShadow
          userData={{ isWall: true }}
        >
          <boxGeometry args={[w.len, w.h, w.thickness]} />
          <meshStandardMaterial
            color={wallMat.color}
            map={wallMat.map}
            roughness={wallMat.roughness}
          />
        </mesh>
      ))}
      {/* Door frames — coloured panels showing where the opening is. Entry/exit
          use a semi-transparent slab so you can see through it; loading uses a
          striped shutter look. */}
      {(wallsGeom.doors || []).map((d) => (
        <DoorMesh key={d.id} door={d} />
      ))}
    </group>
  );
}

// Door colours + kinds — mirrored from PropertiesPanel.DOOR_KINDS so this
// file can render doors without importing React JSX.
const DOOR_3D_COLORS = {
  entry:     "#22c55e",
  exit:      "#eab308",
  fire_exit: "#ef4444",
  loading:   "#06b6d4",
};

function DoorMesh({ door }) {
  const color = DOOR_3D_COLORS[door.kind] || "#22c55e";
  if (door.kind === "loading") {
    // Shutter — a bar of horizontal slats
    const slats = 8;
    const slatH = door.h / slats;
    return (
      <group position={[door.cx, door.h / 2, door.cy]} rotation={[0, -door.angle, 0]}>
        {Array.from({ length: slats }).map((_, i) => (
          <mesh key={i} position={[0, -door.h / 2 + slatH * (i + 0.5), 0]} receiveShadow>
            <boxGeometry args={[door.len, slatH * 0.9, door.thickness * 0.6]} />
            <meshStandardMaterial color={color} metalness={0.55} roughness={0.35} />
          </mesh>
        ))}
        {/* Track rails on either side */}
        {[-1, 1].map((s) => (
          <mesh key={`rail-${s}`} position={[s * door.len / 2, 0, 0]}>
            <boxGeometry args={[0.05, door.h, door.thickness * 0.8]} />
            <meshStandardMaterial color="#334155" metalness={0.6} />
          </mesh>
        ))}
      </group>
    );
  }
  // Entry / exit / fire — single door leaf, semi-transparent tint
  return (
    <group position={[door.cx, door.h / 2, door.cy]} rotation={[0, -door.angle, 0]}>
      {/* Door leaf */}
      <mesh receiveShadow>
        <boxGeometry args={[door.len * 0.92, door.h * 0.95, door.thickness * 0.4]} />
        <meshStandardMaterial color={color} transparent opacity={0.75} roughness={0.6} metalness={0.15} />
      </mesh>
      {/* Frame */}
      <mesh>
        <boxGeometry args={[door.len, door.h, door.thickness * 0.9]} />
        <meshStandardMaterial color={color} transparent opacity={0.15} />
      </mesh>
      {/* Handle */}
      <mesh position={[door.len * 0.35, 0, door.thickness * 0.35]}>
        <boxGeometry args={[0.03, 0.12, 0.03]} />
        <meshStandardMaterial color="#111827" metalness={0.7} />
      </mesh>
    </group>
  );
}

function PalletRack3D({ obj }) {
  const p = obj.props;
  const bayLen = (p.bay_length || 2700) * MM;
  const numBays = p.num_bays || 1;
  const depth = (p.depth || 1100) * MM;
  const height = (p.height || 6000) * MM;
  const levels = p.levels || 4;
  const beamSpacing = (p.beam_spacing || 1500) * MM;
  const totalLen = bayLen * numBays;

  // Realistic colour scheme per the racking reference:
  // - blue uprights & cross-bracing
  // - orange (user-selected) beams
  // - yellow base / footplate guards
  // - gray steel decking sheets between front and back beams
  const UPRIGHT = "#1d4ed8"; // blue
  const BEAM = obj.color || "#f97316"; // orange
  const FOOT = "#facc15"; // yellow
  const DECK = "#9ca3af"; // gray steel
  const BRACE = "#1e40af"; // darker blue for diagonals

  const els = [];
  const uprightW = 0.12; // metres
  const uprightD = 0.1;
  const footH = 0.4;

  // ------- Uprights & footings -------
  for (let i = 0; i <= numBays; i++) {
    const x = -totalLen / 2 + i * bayLen;
    const zFront = -depth / 2 + uprightD / 2;
    const zBack = depth / 2 - uprightD / 2;

    // Yellow footplate guards (short, at base)
    els.push(<mesh key={`f-${i}-f`} position={[x, footH / 2, zFront]} castShadow><boxGeometry args={[uprightW * 1.6, footH, uprightD * 1.6]} /><meshStandardMaterial color={FOOT} roughness={0.5} metalness={0.2} /></mesh>);
    els.push(<mesh key={`f-${i}-b`} position={[x, footH / 2, zBack]} castShadow><boxGeometry args={[uprightW * 1.6, footH, uprightD * 1.6]} /><meshStandardMaterial color={FOOT} roughness={0.5} metalness={0.2} /></mesh>);

    // Blue uprights (above the footings)
    els.push(<mesh key={`u-${i}-f`} position={[x, footH + (height - footH) / 2, zFront]} castShadow><boxGeometry args={[uprightW, height - footH, uprightD]} /><meshStandardMaterial color={UPRIGHT} roughness={0.45} metalness={0.55} /></mesh>);
    els.push(<mesh key={`u-${i}-b`} position={[x, footH + (height - footH) / 2, zBack]} castShadow><boxGeometry args={[uprightW, height - footH, uprightD]} /><meshStandardMaterial color={UPRIGHT} roughness={0.45} metalness={0.55} /></mesh>);

    // Cross-bracing (X pattern between front and back uprights, segmented along height)
    const segs = Math.max(2, Math.round((height - footH) / 0.9));
    const segH = (height - footH) / segs;
    const braceLen = Math.hypot(depth - uprightD, segH);
    const angle = Math.atan2(depth - uprightD, segH);
    for (let s = 0; s < segs; s++) {
      const yMid = footH + s * segH + segH / 2;
      // Diagonal "/"
      els.push(
        <mesh key={`br-${i}-${s}-a`} position={[x, yMid, 0]} rotation={[angle, 0, 0]}>
          <boxGeometry args={[0.04, 0.04, braceLen]} />
          <meshStandardMaterial color={BRACE} roughness={0.5} metalness={0.4} />
        </mesh>
      );
      // Diagonal "\"
      els.push(
        <mesh key={`br-${i}-${s}-b`} position={[x, yMid, 0]} rotation={[-angle, 0, 0]}>
          <boxGeometry args={[0.04, 0.04, braceLen]} />
          <meshStandardMaterial color={BRACE} roughness={0.5} metalness={0.4} />
        </mesh>
      );
    }
  }

  // ------- Beams + decking per level -------
  // Auto-fit: if the user's beam spacing * levels would overflow the height,
  // squeeze the spacing so all requested levels are visible. Otherwise honour it.
  const beamH = 0.12;
  const beamD = 0.06;
  const usableH = height - 0.3; // leave a little room above the top beam
  const effectiveSpacing = Math.min(beamSpacing, usableH / (levels + 0.15));
  for (let lvl = 1; lvl <= levels; lvl++) {
    const y = lvl * effectiveSpacing;
    for (let i = 0; i < numBays; i++) {
      const cx = -totalLen / 2 + (i + 0.5) * bayLen;
      // Orange front + back beams
      els.push(<mesh key={`bf-${lvl}-${i}`} position={[cx, y, -depth / 2 + beamD / 2 + uprightD / 2]} castShadow><boxGeometry args={[bayLen - uprightW, beamH, beamD]} /><meshStandardMaterial color={BEAM} roughness={0.45} metalness={0.45} /></mesh>);
      els.push(<mesh key={`bb-${lvl}-${i}`} position={[cx, y, depth / 2 - beamD / 2 - uprightD / 2]} castShadow><boxGeometry args={[bayLen - uprightW, beamH, beamD]} /><meshStandardMaterial color={BEAM} roughness={0.45} metalness={0.45} /></mesh>);
      // Gray steel decking between the two beams
      els.push(
        <mesh key={`d-${lvl}-${i}`} position={[cx, y + beamH / 2 + 0.012, 0]} receiveShadow castShadow>
          <boxGeometry args={[bayLen - uprightW * 1.2, 0.025, depth - uprightD * 1.2 - beamD]} />
          <meshStandardMaterial color={DECK} roughness={0.65} metalness={0.4} />
        </mesh>
      );
    }
  }

  // ------- Top tie-beam between front-back uprights -------
  for (let i = 0; i <= numBays; i++) {
    const x = -totalLen / 2 + i * bayLen;
    els.push(
      <mesh key={`tt-${i}`} position={[x, height - 0.05, 0]}>
        <boxGeometry args={[uprightW, 0.06, depth - uprightD]} />
        <meshStandardMaterial color={UPRIGHT} roughness={0.5} metalness={0.45} />
      </mesh>
    );
  }

  return <group>{els}</group>;
}

function SlottedAngleRack3D({ obj }) {
  const p = obj.props;
  const w = (p.width || 1200) * MM;
  const d = (p.depth || 600) * MM;
  const h = (p.height || 2000) * MM;
  const shelves = p.shelves || 5;
  const units = p.units_in_row || 1;
  const totalLen = w * units;

  const els = [];
  // Uprights at each unit boundary
  for (let i = 0; i <= units; i++) {
    const x = -totalLen / 2 + i * w;
    els.push(<mesh key={`uA${i}`} position={[x, h / 2, -d / 2]}><boxGeometry args={[0.05, h, 0.05]} /><meshStandardMaterial color={obj.color} metalness={0.3} /></mesh>);
    els.push(<mesh key={`uB${i}`} position={[x, h / 2, d / 2]}><boxGeometry args={[0.05, h, 0.05]} /><meshStandardMaterial color={obj.color} metalness={0.3} /></mesh>);
  }
  // Shelves
  for (let s = 0; s < shelves; s++) {
    const y = (h / (shelves)) * (s + 0.5);
    els.push(<mesh key={`sh${s}`} position={[0, y, 0]}><boxGeometry args={[totalLen - 0.02, 0.02, d - 0.02]} /><meshStandardMaterial color="#cbd5e1" /></mesh>);
  }
  return <group>{els}</group>;
}

function DriveInRack3D({ obj }) {
  const p = obj.props;
  const lanes = p.lanes || 3;
  const laneW = (p.lane_width || 1300) * MM;
  const totalW = lanes * laneW;
  const depth = ((p.pallet_depth || 1100) * (p.depth_pallets || 1)) * MM;
  const height = (p.height || 7000) * MM;
  const levels = p.levels || 4;

  const UPRIGHT = "#1d4ed8";
  const BEAM = obj.color || "#f97316";
  const FOOT = "#facc15";

  const uprightW = 0.14;
  const uprightD = 0.14;
  const footH = 0.4;
  const els = [];

  // ---- Uprights: at every lane boundary, front AND back.
  // (Drive-in racks are intentionally bracing-free between front and back so a
  //  forklift can drive straight in — we leave the horizontal top tie-beams to
  //  provide the racking-out stability.)
  for (let i = 0; i <= lanes; i++) {
    const x = -totalW / 2 + i * laneW;
    const zFront = -depth / 2 + uprightD / 2;
    const zBack = depth / 2 - uprightD / 2;
    // Footplates
    els.push(<mesh key={`f-${i}-f`} position={[x, footH / 2, zFront]} castShadow><boxGeometry args={[uprightW * 1.6, footH, uprightD * 1.6]} /><meshStandardMaterial color={FOOT} roughness={0.5} metalness={0.2} /></mesh>);
    els.push(<mesh key={`f-${i}-b`} position={[x, footH / 2, zBack]} castShadow><boxGeometry args={[uprightW * 1.6, footH, uprightD * 1.6]} /><meshStandardMaterial color={FOOT} roughness={0.5} metalness={0.2} /></mesh>);
    // Uprights
    els.push(<mesh key={`u-${i}-f`} position={[x, footH + (height - footH) / 2, zFront]} castShadow><boxGeometry args={[uprightW, height - footH, uprightD]} /><meshStandardMaterial color={UPRIGHT} roughness={0.45} metalness={0.55} /></mesh>);
    els.push(<mesh key={`u-${i}-b`} position={[x, footH + (height - footH) / 2, zBack]} castShadow><boxGeometry args={[uprightW, height - footH, uprightD]} /><meshStandardMaterial color={UPRIGHT} roughness={0.45} metalness={0.55} /></mesh>);
  }

  // ---- Load rails: horizontal orange rails running along the DEPTH inside each lane.
  // Distribute levels evenly across height so all requested levels are visible.
  const railH = 0.08;
  const railStep = (height - 0.3) / (levels + 0.15);
  for (let lv = 1; lv <= levels; lv++) {
    const y = lv * railStep;
    for (let i = 0; i < lanes; i++) {
      const cx = -totalW / 2 + (i + 0.5) * laneW;
      const railOffset = laneW * 0.35;
      els.push(<mesh key={`rL-${lv}-${i}`} position={[cx - railOffset, y, 0]} castShadow><boxGeometry args={[0.08, railH, depth]} /><meshStandardMaterial color={BEAM} roughness={0.45} metalness={0.45} /></mesh>);
      els.push(<mesh key={`rR-${lv}-${i}`} position={[cx + railOffset, y, 0]} castShadow><boxGeometry args={[0.08, railH, depth]} /><meshStandardMaterial color={BEAM} roughness={0.45} metalness={0.45} /></mesh>);
    }
  }

  // ---- Top tie beams between front and back uprights (blue)
  for (let i = 0; i <= lanes; i++) {
    const x = -totalW / 2 + i * laneW;
    els.push(<mesh key={`tt-${i}`} position={[x, height - 0.05, 0]}><boxGeometry args={[uprightW, 0.06, depth - uprightD]} /><meshStandardMaterial color={UPRIGHT} roughness={0.5} metalness={0.45} /></mesh>);
  }

  return <group>{els}</group>;
}

function CantileverRack3D({ obj }) {
  const p = obj.props;
  const cols = p.columns || 4;
  const colSpacing = (p.column_spacing || 1500) * MM;
  const armLen = (p.arm_length || 1200) * MM;
  const height = (p.height || 4000) * MM;
  const levels = p.levels || 4;
  const totalLen = cols * colSpacing;
  const doubleSided = !!p.double_sided;

  const UPRIGHT = "#1d4ed8";
  const ARM = obj.color || "#f97316";
  const FOOT = "#facc15";
  const BRACE = "#1e40af";

  const uprightW = 0.16;
  const uprightD = 0.16;
  const footH = 0.35;
  const els = [];

  // Column uprights + yellow footplates + long structural base
  for (let i = 0; i <= cols; i++) {
    const x = -totalLen / 2 + i * colSpacing;
    // Yellow footplate at base
    const baseD = doubleSided ? armLen * 1.7 : armLen * 1.1;
    els.push(<mesh key={`f-${i}`} position={[x, footH / 2, 0]} castShadow><boxGeometry args={[uprightW * 2.2, footH, baseD]} /><meshStandardMaterial color={FOOT} roughness={0.5} metalness={0.25} /></mesh>);
    // Blue vertical upright
    els.push(<mesh key={`u-${i}`} position={[x, footH + (height - footH) / 2, 0]} castShadow><boxGeometry args={[uprightW, height - footH, uprightD]} /><meshStandardMaterial color={UPRIGHT} roughness={0.45} metalness={0.55} /></mesh>);
  }

  // X-cross-bracing between adjacent columns (front face only if single-sided)
  for (let i = 0; i < cols; i++) {
    const xA = -totalLen / 2 + i * colSpacing;
    const xB = -totalLen / 2 + (i + 1) * colSpacing;
    const midX = (xA + xB) / 2;
    const braceLen = Math.hypot(colSpacing, height - footH - 0.4);
    const angle = Math.atan2(colSpacing, height - footH - 0.4);
    const yMid = footH + (height - footH) / 2;
    // Position the brace slightly behind the arm face
    const zBrace = doubleSided ? 0 : -uprightD * 1.5;
    els.push(<mesh key={`br-${i}-a`} position={[midX, yMid, zBrace]} rotation={[0, 0, angle]}><boxGeometry args={[0.05, braceLen, 0.05]} /><meshStandardMaterial color={BRACE} roughness={0.5} metalness={0.4} /></mesh>);
    els.push(<mesh key={`br-${i}-b`} position={[midX, yMid, zBrace]} rotation={[0, 0, -angle]}><boxGeometry args={[0.05, braceLen, 0.05]} /><meshStandardMaterial color={BRACE} roughness={0.5} metalness={0.4} /></mesh>);
  }

  // Orange cantilever arms per level, per column
  const armThickness = 0.1;
  const armH = 0.14;
  for (let i = 0; i <= cols; i++) {
    const x = -totalLen / 2 + i * colSpacing;
    for (let lv = 1; lv <= levels; lv++) {
      const y = (height / (levels + 0.5)) * lv;
      // Front arm
      els.push(<mesh key={`aF-${i}-${lv}`} position={[x, y, armLen / 2]} castShadow><boxGeometry args={[armThickness, armH, armLen]} /><meshStandardMaterial color={ARM} roughness={0.45} metalness={0.5} /></mesh>);
      // Diagonal support (small triangular gusset — modelled as a slim angled brace) from column to arm tip
      els.push(<mesh key={`gF-${i}-${lv}`} position={[x, y - armH / 2 - 0.05, armLen / 2]} rotation={[Math.atan2(0.3, armLen), 0, 0]}><boxGeometry args={[armThickness * 0.6, 0.04, armLen]} /><meshStandardMaterial color={BRACE} roughness={0.5} metalness={0.5} /></mesh>);
      if (doubleSided) {
        els.push(<mesh key={`aB-${i}-${lv}`} position={[x, y, -armLen / 2]} castShadow><boxGeometry args={[armThickness, armH, armLen]} /><meshStandardMaterial color={ARM} roughness={0.45} metalness={0.5} /></mesh>);
        els.push(<mesh key={`gB-${i}-${lv}`} position={[x, y - armH / 2 - 0.05, -armLen / 2]} rotation={[-Math.atan2(0.3, armLen), 0, 0]}><boxGeometry args={[armThickness * 0.6, 0.04, armLen]} /><meshStandardMaterial color={BRACE} roughness={0.5} metalness={0.5} /></mesh>);
      }
    }
  }

  return <group>{els}</group>;
}

function Mezzanine3D({ obj }) {
  const p = obj.props;
  const len = (p.length || 6000) * MM;
  const wid = (p.width || 4000) * MM;
  const fh = (p.floor_height || 2500) * MM;
  const slab = (p.slab_thickness || 200) * MM;
  const grid = (p.column_grid || 3000) * MM;
  const shape = p.shape || "rectangle";
  const notchL = (p.notch_length || 3000) * MM;
  const notchW = (p.notch_width || 2500) * MM;
  const notchCorner = p.notch_corner || "front_right";
  const isL = shape === "l_shape";
  // Notch corner signs — "front" = -Z, "back" = +Z, "left" = -X, "right" = +X
  const signX = notchCorner.includes("right") ? 1 : -1;
  const signZ = notchCorner.includes("back")  ? 1 : -1;
  // Local helper: is this (x,z) point inside the notch cut-out?
  const nL = Math.min(notchL, len - 500 * MM);
  const nW = Math.min(notchW, wid - 500 * MM);
  const inNotch = (x, z) => {
    if (!isL) return false;
    const insideX = signX === 1 ? x > (len / 2 - nL) : x < (-len / 2 + nL);
    const insideZ = signZ === 1 ? z > (wid / 2 - nW) : z < (-wid / 2 + nW);
    return insideX && insideZ;
  };

  const els = [];

  // ---------- Slab ----------
  if (!isL) {
    els.push(<mesh key="slab" position={[0, fh, 0]} castShadow receiveShadow><boxGeometry args={[len, slab, wid]} /><meshStandardMaterial color={obj.color} metalness={0.3} roughness={0.6} /></mesh>);
  } else {
    // L = full-length back strip + (length - notchL) front strip.
    // Back strip: full length, wid - notchW deep, shifted away from the notch side in Z.
    els.push(
      <mesh key="slab-A" position={[0, fh, -signZ * nW / 2]} castShadow receiveShadow>
        <boxGeometry args={[len, slab, wid - nW]} />
        <meshStandardMaterial color={obj.color} metalness={0.3} roughness={0.6} />
      </mesh>
    );
    // Front strip: (len - notchL) long, notchW deep, shifted opposite to the notch in X.
    els.push(
      <mesh key="slab-B" position={[-signX * nL / 2, fh, signZ * (wid / 2 - nW / 2)]} castShadow receiveShadow>
        <boxGeometry args={[len - nL, slab, nW]} />
        <meshStandardMaterial color={obj.color} metalness={0.3} roughness={0.6} />
      </mesh>
    );
  }

  // ---------- Columns (skip inside notch) ----------
  const nCx = Math.max(2, Math.round(len / grid) + 1);
  const nCy = Math.max(2, Math.round(wid / grid) + 1);
  for (let i = 0; i < nCx; i++) {
    for (let j = 0; j < nCy; j++) {
      const x = -len / 2 + (i / (nCx - 1)) * len;
      const z = -wid / 2 + (j / (nCy - 1)) * wid;
      if (inNotch(x, z)) continue;
      els.push(<mesh key={`c${i}-${j}`} position={[x, fh / 2, z]} castShadow><boxGeometry args={[0.18, fh, 0.18]} /><meshStandardMaterial color="#3a3a3a" metalness={0.5} /></mesh>);
    }
  }

  // ---------- Perimeter vertices (top-down, in local mezz coords) ----------
  // Rectangle: 4 corners; L: 6 corners with the notch cut out.
  const perim = (() => {
    const L = len / 2, W = wid / 2;
    if (!isL) return [[-L, -W], [L, -W], [L, W], [-L, W]];
    // Build vertices in counter-clockwise order starting from bottom-left,
    // rerouting around the cut corner. Handle each notchCorner variant.
    if (signX === 1 && signZ === -1) {
      // Front-right notch removed
      return [[-L, -W], [L - nL, -W], [L - nL, -W + nW], [L, -W + nW], [L, W], [-L, W]];
    }
    if (signX === -1 && signZ === -1) {
      // Front-left notch removed
      return [[-L, -W + nW], [-L + nL, -W + nW], [-L + nL, -W], [L, -W], [L, W], [-L, W]];
    }
    if (signX === 1 && signZ === 1) {
      // Back-right notch removed
      return [[-L, -W], [L, -W], [L, W - nW], [L - nL, W - nW], [L - nL, W], [-L, W]];
    }
    // Back-left notch removed
    return [[-L, -W], [L, -W], [L, W], [-L + nL, W], [-L + nL, W - nW], [-L, W - nW]];
  })();

  // ---------- Railing (traces the perimeter, one segment per edge) ----------
  if (p.railing) {
    const rh = 1.0;
    const rY = fh + slab + rh / 2;
    for (let i = 0; i < perim.length; i++) {
      const a = perim[i];
      const b = perim[(i + 1) % perim.length];
      const dx = b[0] - a[0], dz = b[1] - a[1];
      const len2 = Math.hypot(dx, dz);
      if (len2 < 0.01) continue;
      const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      const rotY = Math.atan2(dz, dx);
      els.push(
        <mesh key={`rail-${i}`} position={[mid[0], rY, mid[1]]} rotation={[0, -rotY, 0]}>
          <boxGeometry args={[len2, rh, 0.04]} />
          <meshStandardMaterial color="#666" />
        </mesh>
      );
    }
  }

  // ---------- Stairs ----------
  const stairW = (p.stair_width || 1000) * MM;
  const stairPos = p.stair_position || "front_left";
  const direction = p.stair_direction || "outward";
  const stepCount = Math.max(6, Math.round(fh / 0.19)); // ~190mm rise per step
  const stepRise = fh / stepCount;
  const stepRun = 0.28; // 280mm tread
  const totalRun = stepCount * stepRun;
  const stepDepth = stepRun + 0.02;

  // Edge on the mezzanine perimeter where the top of stair connects
  // (edgeAxis / edgePos / outwardDir)
  //   edgeAxis  = 'x' or 'z'  (which floor axis the stairs travel along)
  //   startX/Z  = position of the FIRST (bottom) step centre
  //   dirSign   = +1 or -1 : direction of travel from bottom to top
  let stairAxis = "z"; // z = front↔back travel; x = left↔right travel
  let startX = 0, startZ = 0, dirSign = 1;

  const edgeOffset = 0.4; // small gap so the top step visibly lands on the slab

  // Anchor point on mezzanine edge where the stair top meets.
  let topX = 0, topZ = 0;
  const cornerInset = stairW / 2 + 0.2;

  if (stairPos.startsWith("front_") || stairPos.startsWith("back_")) {
    // The stair top lies on the front (-Z) or back (+Z) edge.
    const zEdge = stairPos.startsWith("front_") ? -wid / 2 : wid / 2;
    topZ = zEdge;
    // Which end of that edge
    if (stairPos.endsWith("_left"))       topX = -len / 2 + cornerInset;
    else if (stairPos.endsWith("_center")) topX = 0;
    else if (stairPos.endsWith("_right"))  topX = len / 2 - cornerInset;

    if (direction === "outward") {
      // Stairs project away from the mezzanine
      stairAxis = "z";
      dirSign = stairPos.startsWith("front_") ? -1 : 1; // outward
      startX = topX;
      startZ = topZ + dirSign * (totalRun + edgeOffset);
    } else {
      // along the edge
      stairAxis = "x";
      dirSign = stairPos.endsWith("_right") ? -1 : 1; // run from far end toward the anchor
      startZ = topZ + (stairPos.startsWith("front_") ? -0.3 : 0.3); // just outside slab
      startX = topX + dirSign * totalRun;
    }
  } else {
    // side_left  → left edge (-X);   side_right → right edge (+X)
    const xEdge = stairPos === "side_left" ? -len / 2 : len / 2;
    topX = xEdge;
    topZ = 0;
    if (direction === "outward") {
      stairAxis = "x";
      dirSign = stairPos === "side_left" ? -1 : 1;
      startZ = topZ;
      startX = topX + dirSign * (totalRun + edgeOffset);
    } else {
      stairAxis = "z";
      dirSign = 1;
      startX = topX + (stairPos === "side_left" ? -0.3 : 0.3);
      startZ = topZ - totalRun / 2;
    }
  }

  // Render steps: each is a small box, ascending from the ground toward the mezzanine.
  // stepBox: X-axis is PERPENDICULAR to travel (= full stair width); Z-axis is ALONG travel (= one tread).
  for (let s = 0; s < stepCount; s++) {
    const t = s + 0.5; // centre index
    const stepY = stepRise * t;
    const along = -dirSign * (t * stepRun);
    let cx = startX;
    let cz = startZ;
    if (stairAxis === "z") cz = startZ + along;
    else cx = startX + along;

    const stepXLen = stairAxis === "z" ? stairW : stepDepth;
    const stepZLen = stairAxis === "z" ? stepDepth : stairW;
    els.push(
      <mesh key={`step-${s}`} position={[cx, stepY, cz]} castShadow receiveShadow>
        <boxGeometry args={[stepXLen, stepRise * 0.98, stepZLen]} />
        <meshStandardMaterial color={obj.color} roughness={0.5} metalness={0.4} />
      </mesh>
    );
  }

  // Landing at top: a small platform right on the mezzanine edge where the stairs arrive.
  if (p.stair_landing) {
    const landingLen = stairAxis === "z" ? 0.6 : stairW + 0.2;
    const landingWid = stairAxis === "z" ? stairW + 0.2 : 0.6;
    els.push(
      <mesh key="landing" position={[topX, fh + slab / 2 + 0.02, topZ]}>
        <boxGeometry args={[landingLen, 0.06, landingWid]} />
        <meshStandardMaterial color="#94a3b8" roughness={0.6} metalness={0.4} />
      </mesh>
    );
  }

  // Slim handrails — a thin diagonal top rail along each side of the flight,
  // plus a couple of vertical posts. Nothing bigger than 4-5 cm thick, so it reads
  // as a hand-rail instead of a wall panel.
  if (p.stair_handrails) {
    const railH = 1.0;   // handrail sits 1 m above the treads
    const rotY = Math.atan2(topZ - startZ, topX - startX);
    const perpX = Math.sin(rotY);
    const perpZ = -Math.cos(rotY);
    const half = stairW / 2 + 0.02;
    const railThickness = 0.04;

    // End-points of the two sloping top rails (from bottom of flight to top).
    // A = bottom  (at ground + railH), B = top (at fh + railH).
    const buildRail = (side) => {
      const s = side; // +1 or -1
      const A = [startX + perpX * half * s, railH, startZ + perpZ * half * s];
      const B = [topX + perpX * half * s,   fh + railH, topZ + perpZ * half * s];
      const dx = B[0] - A[0], dy = B[1] - A[1], dz = B[2] - A[2];
      const len = Math.hypot(dx, dy, dz);
      const mid = [(A[0] + B[0]) / 2, (A[1] + B[1]) / 2, (A[2] + B[2]) / 2];
      // Orient a slim box (long axis X) between A and B via quaternion.
      const dir = new THREE.Vector3(dx, dy, dz).normalize();
      const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(1, 0, 0), dir);
      return { A, B, mid, len, q };
    };

    [1, -1].forEach((s) => {
      const rail = buildRail(s);
      els.push(
        <mesh key={`rail-top-${s}`} position={rail.mid} quaternion={[rail.q.x, rail.q.y, rail.q.z, rail.q.w]}>
          <boxGeometry args={[rail.len, railThickness, railThickness]} />
          <meshStandardMaterial color="#6b7280" metalness={0.5} roughness={0.4} />
        </mesh>
      );
      // Vertical posts at bottom, middle, top
      [0, 0.5, 1].forEach((t, idx) => {
        const px = rail.A[0] + (rail.B[0] - rail.A[0]) * t;
        const py = rail.A[1] + (rail.B[1] - rail.A[1]) * t;
        const pz = rail.A[2] + (rail.B[2] - rail.A[2]) * t;
        // Post runs from tread up to the top rail (approx `py`).
        // Bottom is at tread level = py - railH (roughly), so post length = railH.
        els.push(
          <mesh key={`post-${s}-${idx}`} position={[px, py - railH / 2, pz]}>
            <boxGeometry args={[railThickness, railH, railThickness]} />
            <meshStandardMaterial color="#6b7280" metalness={0.5} roughness={0.4} />
          </mesh>
        );
      });
    });
  }

  return <group>{els}</group>;
}

function Forklift3D({ obj }) {
  const p = obj.props;
  const len = (p.length || 2400) * MM;
  const w = (p.width || 1100) * MM;
  const h = (p.height || 2200) * MM;
  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.5, 0]}><boxGeometry args={[len * 0.6, 0.8, w]} /><meshStandardMaterial color={obj.color} metalness={0.3} /></mesh>
      {/* Cab */}
      <mesh position={[-len * 0.05, 1.2, 0]}><boxGeometry args={[len * 0.4, 1.0, w * 0.85]} /><meshStandardMaterial color={obj.color} metalness={0.3} /></mesh>
      {/* Mast */}
      <mesh position={[len * 0.45, h / 2, 0]}><boxGeometry args={[0.1, h, w * 0.7]} /><meshStandardMaterial color="#222" /></mesh>
      {/* Forks */}
      <mesh position={[len * 0.55, 0.15, w / 4]}><boxGeometry args={[len * 0.3, 0.05, 0.08]} /><meshStandardMaterial color="#444" metalness={0.7} /></mesh>
      <mesh position={[len * 0.55, 0.15, -w / 4]}><boxGeometry args={[len * 0.3, 0.05, 0.08]} /><meshStandardMaterial color="#444" metalness={0.7} /></mesh>
    </group>
  );
}

function HandPalletTruck3D({ obj }) {
  const p = obj.props;
  const len = (p.length || 1500) * MM;
  const w = (p.width || 550) * MM;
  return (
    <group>
      <mesh position={[0, 0.07, w / 4]}><boxGeometry args={[len, 0.08, 0.1]} /><meshStandardMaterial color={obj.color} /></mesh>
      <mesh position={[0, 0.07, -w / 4]}><boxGeometry args={[len, 0.08, 0.1]} /><meshStandardMaterial color={obj.color} /></mesh>
      <mesh position={[-len / 2 + 0.1, 0.6, 0]}><boxGeometry args={[0.1, 1.1, 0.4]} /><meshStandardMaterial color={obj.color} /></mesh>
    </group>
  );
}

function Pallet3D({ obj }) {
  const p = obj.props;
  const len = (p.length || 1200) * MM;
  const w = (p.width || 1000) * MM;
  const h = (p.height || 150) * MM;
  return (
    <group>
      <mesh position={[0, h / 2, 0]}><boxGeometry args={[len, h, w]} /><meshStandardMaterial color="#a16207" roughness={0.95} /></mesh>
    </group>
  );
}

function Box3D({ obj, length, width, height, color }) {
  return (
    <mesh position={[0, height / 2, 0]}><boxGeometry args={[length, height, width]} /><meshStandardMaterial color={color} /></mesh>
  );
}

// -------------------- SUPERMARKET 3D COMPONENTS --------------------

function WallRack3D({ obj }) {
  const p = obj.props;
  const bayW = (p.width || 1200) * MM;
  const bays = p.bays || 1;
  const totalLen = bayW * bays;
  const d = (p.depth || 500) * MM;
  const h = (p.height || 2100) * MM;
  const shelves = p.shelves || 5;
  const backKind = p.back_panel || "solid";

  const els = [];
  const BODY = obj.color;
  const BACK = backKind === "solid" ? BODY : "#334155";
  const SHELF = "#e2e8f0";
  const uprightT = 0.05;

  // Back panel
  els.push(
    <mesh key="back" position={[0, h / 2, -d / 2 + uprightT / 2]} receiveShadow>
      <boxGeometry args={[totalLen, h, uprightT]} />
      <meshStandardMaterial color={BACK} roughness={0.7} metalness={0.15} />
    </mesh>
  );
  // Pegboard dots
  if (backKind === "pegboard") {
    const rows = 8, cols = Math.max(4, Math.round(totalLen / 0.12));
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = -totalLen / 2 + ((c + 0.5) / cols) * totalLen;
        const y = (h * 0.15) + ((r + 0.5) / rows) * (h * 0.7);
        els.push(<mesh key={`peg-${r}-${c}`} position={[x, y, -d / 2 + 0.03]}><cylinderGeometry args={[0.006, 0.006, 0.02, 6]} /><meshStandardMaterial color="#111827" /></mesh>);
      }
    }
  }
  // Slatwall horizontal ribs
  if (backKind === "slatwall") {
    for (let i = 1; i < 10; i++) {
      const y = (h / 10) * i;
      els.push(<mesh key={`slat-${i}`} position={[0, y, -d / 2 + 0.04]}><boxGeometry args={[totalLen * 0.98, 0.015, 0.02]} /><meshStandardMaterial color="#475569" /></mesh>);
    }
  }
  // End uprights + bay dividers
  for (let i = 0; i <= bays; i++) {
    const x = -totalLen / 2 + i * bayW;
    els.push(<mesh key={`u-${i}`} position={[x, h / 2, 0]}><boxGeometry args={[uprightT, h, d]} /><meshStandardMaterial color={BODY} metalness={0.2} roughness={0.6} /></mesh>);
  }
  // Base kick-plate
  els.push(<mesh key="kick" position={[0, 0.05, d / 4]}><boxGeometry args={[totalLen, 0.1, d / 2]} /><meshStandardMaterial color={BODY} metalness={0.3} roughness={0.5} /></mesh>);
  // Shelves — angled forward slightly for merchandise visibility
  for (let s = 1; s <= shelves; s++) {
    const y = 0.15 + (h - 0.4) * (s / shelves);
    els.push(<mesh key={`s-${s}`} position={[0, y, 0.02]} castShadow><boxGeometry args={[totalLen - 0.06, 0.02, d - 0.06]} /><meshStandardMaterial color={SHELF} roughness={0.55} metalness={0.2} /></mesh>);
    // shelf lip at the front
    els.push(<mesh key={`sl-${s}`} position={[0, y + 0.02, d / 2 - 0.02]}><boxGeometry args={[totalLen - 0.06, 0.05, 0.01]} /><meshStandardMaterial color="#94a3b8" /></mesh>);
  }
  return <group>{els}</group>;
}

function GondolaRack3D({ obj }) {
  const p = obj.props;
  const bayW = (p.width || 1200) * MM;
  const bays = p.bays || 3;
  const totalLen = bayW * bays;
  const totalD = (p.depth || 900) * MM;
  const h = (p.height || 1800) * MM;
  const shelves = p.shelves || 5;
  const dbl = p.double_sided !== false;
  const halfD = dbl ? totalD / 2 : totalD;

  const els = [];
  const BODY = obj.color;
  const SHELF = "#e2e8f0";

  // Central spine
  els.push(<mesh key="spine" position={[0, h / 2, 0]}><boxGeometry args={[totalLen, h, 0.06]} /><meshStandardMaterial color={BODY} metalness={0.3} roughness={0.55} /></mesh>);
  // End caps (uprights)
  for (let i = 0; i <= bays; i++) {
    const x = -totalLen / 2 + i * bayW;
    els.push(<mesh key={`u-${i}`} position={[x, h / 2, 0]}><boxGeometry args={[0.05, h, totalD]} /><meshStandardMaterial color={BODY} metalness={0.2} roughness={0.6} /></mesh>);
  }
  // Front-facing shelves (one side always)
  for (let s = 1; s <= shelves; s++) {
    const y = 0.15 + (h - 0.4) * (s / shelves);
    els.push(<mesh key={`sf-${s}`} position={[0, y, halfD / 2]} castShadow><boxGeometry args={[totalLen - 0.06, 0.02, halfD - 0.04]} /><meshStandardMaterial color={SHELF} roughness={0.55} metalness={0.2} /></mesh>);
    els.push(<mesh key={`slf-${s}`} position={[0, y + 0.02, halfD - 0.02]}><boxGeometry args={[totalLen - 0.06, 0.05, 0.01]} /><meshStandardMaterial color="#94a3b8" /></mesh>);
    if (dbl) {
      els.push(<mesh key={`sb-${s}`} position={[0, y, -halfD / 2]} castShadow><boxGeometry args={[totalLen - 0.06, 0.02, halfD - 0.04]} /><meshStandardMaterial color={SHELF} roughness={0.55} metalness={0.2} /></mesh>);
      els.push(<mesh key={`slb-${s}`} position={[0, y + 0.02, -halfD + 0.02]}><boxGeometry args={[totalLen - 0.06, 0.05, 0.01]} /><meshStandardMaterial color="#94a3b8" /></mesh>);
    }
  }
  // Base kick-plates
  els.push(<mesh key="kick-f" position={[0, 0.05, halfD / 2]}><boxGeometry args={[totalLen, 0.1, halfD - 0.04]} /><meshStandardMaterial color={BODY} /></mesh>);
  if (dbl) els.push(<mesh key="kick-b" position={[0, 0.05, -halfD / 2]}><boxGeometry args={[totalLen, 0.1, halfD - 0.04]} /><meshStandardMaterial color={BODY} /></mesh>);
  // End-cap header signage
  if (p.end_cap) {
    els.push(<mesh key="ec" position={[totalLen / 2 + 0.02, h - 0.15, 0]}><boxGeometry args={[0.04, 0.3, totalD]} /><meshStandardMaterial color="#fbbf24" /></mesh>);
  }
  return <group>{els}</group>;
}

function CornerRack3D({ obj }) {
  const p = obj.props;
  const d = (p.depth || 500) * MM;
  const a = (p.leg_a || 1200) * MM;
  const b = (p.leg_b || 1200) * MM;
  const h = (p.height || 2100) * MM;
  const shelves = p.shelves || 5;
  const side = p.corner || "right";
  const sign = side === "left" ? -1 : 1;

  // Legs align with the bounding-box "back" faces (Z = -depth/2 and X = ±length/2 minus depth).
  // Bounding box: length = max(a, d), width = max(b, d). Place the two legs meeting in the corner.
  const boxL = Math.max(a, d);
  const boxW = Math.max(b, d);
  // Leg A runs along X, hugging the -Z back edge. Leg B runs along Z, hugging one side edge.
  const els = [];
  const BODY = obj.color;
  const SHELF = "#e2e8f0";

  // Leg A (horizontal along X)
  const legAcx = -boxL / 2 + a / 2;
  const legAcz = -boxW / 2 + d / 2;
  els.push(<mesh key="legA-back" position={[legAcx, h / 2, legAcz - d / 2 + 0.025]}><boxGeometry args={[a, h, 0.05]} /><meshStandardMaterial color={BODY} metalness={0.2} /></mesh>);
  // Leg B (perpendicular, along Z) placed at ±X side
  const legBcx = sign * (boxL / 2 - d / 2);
  const legBcz = -boxW / 2 + b / 2;
  els.push(<mesh key="legB-back" position={[legBcx + sign * (d / 2 - 0.025), h / 2, legBcz]}><boxGeometry args={[0.05, h, b]} /><meshStandardMaterial color={BODY} metalness={0.2} /></mesh>);

  // Shelves for each leg
  for (let s = 1; s <= shelves; s++) {
    const y = 0.15 + (h - 0.4) * (s / shelves);
    els.push(<mesh key={`shA-${s}`} position={[legAcx, y, legAcz]} castShadow><boxGeometry args={[a - 0.06, 0.02, d - 0.06]} /><meshStandardMaterial color={SHELF} /></mesh>);
    els.push(<mesh key={`shB-${s}`} position={[legBcx, y, legBcz]} castShadow><boxGeometry args={[d - 0.06, 0.02, b - 0.06]} /><meshStandardMaterial color={SHELF} /></mesh>);
  }
  return <group>{els}</group>;
}

function PegboardDisplay3D({ obj }) {
  const p = obj.props;
  const w = (p.width || 1200) * MM;
  const d = (p.depth || 300) * MM;
  const h = (p.height || 2100) * MM;
  const rows = p.hook_rows || 6;
  const cols = p.hooks_per_row || 8;
  const els = [];
  els.push(<mesh key="back" position={[0, h / 2, -d / 2 + 0.02]}><boxGeometry args={[w, h, 0.04]} /><meshStandardMaterial color={obj.color} metalness={0.15} roughness={0.5} /></mesh>);
  // Base
  els.push(<mesh key="base" position={[0, 0.05, 0]}><boxGeometry args={[w, 0.1, d]} /><meshStandardMaterial color={obj.color} /></mesh>);
  // Hooks — small horizontal pins protruding forward
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = -w / 2 + ((c + 0.5) / cols) * w;
      const y = (h * 0.18) + ((r + 0.5) / rows) * (h * 0.7);
      els.push(<mesh key={`hk-${r}-${c}`} position={[x, y, -d / 2 + 0.08]} rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.008, 0.008, 0.12, 6]} /><meshStandardMaterial color="#111827" metalness={0.5} /></mesh>);
    }
  }
  return <group>{els}</group>;
}

function CheckoutCounter3D({ obj }) {
  const p = obj.props;
  const len = (p.length || 2200) * MM;
  const w = (p.width || 700) * MM;
  const h = (p.height || 900) * MM;
  const cashierSide = p.cashier_side || "left";
  const csSign = cashierSide === "left" ? -1 : 1;

  const els = [];
  // Counter body
  els.push(<mesh key="body" position={[0, h / 2, 0]} castShadow receiveShadow>
    <boxGeometry args={[len, h, w]} />
    <meshStandardMaterial color={obj.color} metalness={0.25} roughness={0.55} />
  </mesh>);
  // Top surface accent
  els.push(<mesh key="top" position={[0, h + 0.005, 0]}>
    <boxGeometry args={[len - 0.02, 0.01, w - 0.02]} />
    <meshStandardMaterial color="#111827" roughness={0.4} metalness={0.6} />
  </mesh>);
  // Conveyor belt (front portion)
  if (p.conveyor) {
    els.push(<mesh key="conv" position={[-len * 0.25, h + 0.02, 0]}>
      <boxGeometry args={[len * 0.4, 0.03, w * 0.5]} />
      <meshStandardMaterial color="#0f172a" roughness={0.9} />
    </mesh>);
  }
  // POS screen (small vertical monitor)
  els.push(<mesh key="pos" position={[len * 0.3, h + 0.25, csSign * (w * 0.15)]}>
    <boxGeometry args={[0.28, 0.22, 0.03]} />
    <meshStandardMaterial color="#1e293b" metalness={0.7} roughness={0.3} emissive="#0ea5e9" emissiveIntensity={0.2} />
  </mesh>);
  // POS stand
  els.push(<mesh key="pos-stand" position={[len * 0.3, h + 0.08, csSign * (w * 0.15)]}>
    <boxGeometry args={[0.06, 0.16, 0.06]} />
    <meshStandardMaterial color="#111827" />
  </mesh>);
  // Bagging area (raised platform)
  if (p.bagging_area) {
    els.push(<mesh key="bag" position={[len * 0.4, h + 0.04, 0]}>
      <boxGeometry args={[len * 0.2, 0.05, w - 0.04]} />
      <meshStandardMaterial color="#e2e8f0" />
    </mesh>);
  }
  return <group>{els}</group>;
}

function OpenChiller3D({ obj }) {
  const p = obj.props;
  const len = (p.length || 2500) * MM;
  const w = (p.width || 900) * MM;
  const h = (p.height || 1800) * MM;
  const shelves = p.shelves || 4;
  const els = [];
  const BODY = "#1e293b";
  // Outer body
  els.push(<mesh key="body" position={[0, h / 2, 0]} castShadow>
    <boxGeometry args={[len, h, w]} />
    <meshStandardMaterial color={BODY} metalness={0.4} roughness={0.5} />
  </mesh>);
  // Front opening — inset volume filled with light blue "cold" tint
  els.push(<mesh key="cold" position={[0, h / 2 + 0.05, w / 2 - 0.08]}>
    <boxGeometry args={[len - 0.12, h - 0.4, 0.02]} />
    <meshStandardMaterial color={obj.color} emissive="#38bdf8" emissiveIntensity={0.2} transparent opacity={0.35} />
  </mesh>);
  // Interior shelves visible through the front
  for (let s = 0; s < shelves; s++) {
    const y = 0.25 + (h - 0.5) * (s + 0.5) / shelves;
    els.push(<mesh key={`sh-${s}`} position={[0, y, 0]}><boxGeometry args={[len - 0.14, 0.02, w - 0.2]} /><meshStandardMaterial color="#cbd5e1" /></mesh>);
  }
  // Base plinth
  els.push(<mesh key="plinth" position={[0, 0.06, 0]}><boxGeometry args={[len, 0.12, w + 0.02]} /><meshStandardMaterial color="#334155" /></mesh>);
  // Top canopy with brand strip
  els.push(<mesh key="canopy" position={[0, h - 0.08, 0]}><boxGeometry args={[len + 0.04, 0.16, w + 0.04]} /><meshStandardMaterial color="#0284c7" /></mesh>);
  return <group>{els}</group>;
}

function GlassFreezer3D({ obj }) {
  const p = obj.props;
  const len = (p.length || 1800) * MM;
  const w = (p.width || 800) * MM;
  const h = (p.height || 2000) * MM;
  const doors = p.doors || 3;
  const els = [];
  // Body
  els.push(<mesh key="body" position={[0, h / 2, 0]} castShadow>
    <boxGeometry args={[len, h, w]} />
    <meshStandardMaterial color="#e5e7eb" metalness={0.4} roughness={0.4} />
  </mesh>);
  // Doors (transparent glass)
  const doorW = len / doors;
  for (let i = 0; i < doors; i++) {
    const x = -len / 2 + (i + 0.5) * doorW;
    els.push(<mesh key={`door-${i}`} position={[x, h / 2 + 0.05, w / 2 + 0.01]}>
      <boxGeometry args={[doorW - 0.06, h - 0.2, 0.02]} />
      <meshStandardMaterial color={obj.color} transparent opacity={0.35} metalness={0.6} roughness={0.15} />
    </mesh>);
    // Door handle
    els.push(<mesh key={`hd-${i}`} position={[x + doorW * 0.35, h / 2, w / 2 + 0.03]}>
      <boxGeometry args={[0.03, 0.4, 0.02]} />
      <meshStandardMaterial color="#0f172a" metalness={0.7} />
    </mesh>);
  }
  // Top canopy strip
  els.push(<mesh key="canopy" position={[0, h - 0.08, 0]}><boxGeometry args={[len + 0.04, 0.16, w + 0.04]} /><meshStandardMaterial color="#1d4ed8" /></mesh>);
  return <group>{els}</group>;
}

function ChestFreezer3D({ obj }) {
  const p = obj.props;
  const len = (p.length || 1500) * MM;
  const w = (p.width || 700) * MM;
  const h = (p.height || 900) * MM;
  const els = [];
  els.push(<mesh key="body" position={[0, h / 2, 0]} castShadow>
    <boxGeometry args={[len, h, w]} />
    <meshStandardMaterial color="#f1f5f9" metalness={0.3} roughness={0.5} />
  </mesh>);
  // Sliding glass top
  els.push(<mesh key="top" position={[0, h + 0.01, 0]}>
    <boxGeometry args={[len - 0.06, 0.03, w - 0.06]} />
    <meshStandardMaterial color={obj.color} transparent opacity={0.4} metalness={0.6} roughness={0.15} />
  </mesh>);
  // Brand strip
  els.push(<mesh key="strip" position={[0, h * 0.15, w / 2 + 0.005]}>
    <boxGeometry args={[len - 0.1, 0.1, 0.01]} />
    <meshStandardMaterial color="#1d4ed8" />
  </mesh>);
  return <group>{els}</group>;
}

function ShoppingTrolley3D({ obj }) {
  const p = obj.props;
  const len = (p.length || 950) * MM;
  const w = (p.width || 550) * MM;
  const h = (p.height || 1050) * MM;
  const basketH = h * 0.4;
  const basketBottom = h * 0.45;
  const els = [];
  // Basket walls (mesh look via slim boxes)
  const wireColor = "#94a3b8";
  // Bottom
  els.push(<mesh key="btm" position={[0, basketBottom, 0]}><boxGeometry args={[len * 0.9, 0.02, w * 0.85]} /><meshStandardMaterial color={wireColor} /></mesh>);
  // Sides
  [[0, 1], [0, -1]].forEach(([_, sz], i) =>
    els.push(<mesh key={`sd-${i}`} position={[0, basketBottom + basketH / 2, sz * w * 0.4]}><boxGeometry args={[len * 0.9, basketH, 0.02]} /><meshStandardMaterial color={wireColor} transparent opacity={0.7} /></mesh>)
  );
  els.push(<mesh key="front" position={[len * 0.4, basketBottom + basketH / 2, 0]}><boxGeometry args={[0.02, basketH, w * 0.85]} /><meshStandardMaterial color={wireColor} transparent opacity={0.7} /></mesh>);
  els.push(<mesh key="back" position={[-len * 0.4, basketBottom + basketH / 2, 0]}><boxGeometry args={[0.02, basketH, w * 0.85]} /><meshStandardMaterial color={wireColor} transparent opacity={0.7} /></mesh>);
  // Handle
  els.push(<mesh key="handle" position={[-len * 0.4, h - 0.05, 0]}><boxGeometry args={[0.04, 0.05, w * 0.85]} /><meshStandardMaterial color={obj.color} /></mesh>);
  els.push(<mesh key="hpost-l" position={[-len * 0.4, h * 0.75, w * 0.4]}><boxGeometry args={[0.04, h * 0.35, 0.04]} /><meshStandardMaterial color={obj.color} /></mesh>);
  els.push(<mesh key="hpost-r" position={[-len * 0.4, h * 0.75, -w * 0.4]}><boxGeometry args={[0.04, h * 0.35, 0.04]} /><meshStandardMaterial color={obj.color} /></mesh>);
  // Wheels (small dark boxes)
  const wheelY = 0.04;
  [[1, 1], [1, -1], [-1, 1], [-1, -1]].forEach(([sx, sz], i) =>
    els.push(<mesh key={`wh-${i}`} position={[sx * len * 0.4, wheelY, sz * w * 0.4]}><boxGeometry args={[0.08, 0.08, 0.04]} /><meshStandardMaterial color="#0f172a" /></mesh>)
  );
  return <group>{els}</group>;
}

function PromoBin3D({ obj }) {
  const p = obj.props;
  const diameter = (p.length || 900) * MM;
  const h = (p.height || 700) * MM;
  const r = diameter / 2;
  return (
    <group>
      <mesh position={[0, h / 2, 0]} castShadow>
        <cylinderGeometry args={[r, r * 0.95, h, 24]} />
        <meshStandardMaterial color={obj.color} roughness={0.6} metalness={0.15} />
      </mesh>
      {/* Coloured band around the top */}
      <mesh position={[0, h - 0.08, 0]}>
        <cylinderGeometry args={[r * 1.005, r * 1.005, 0.16, 24, 1, true]} />
        <meshStandardMaterial color="#fbbf24" side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

function BakeryDisplay3D({ obj }) {
  const p = obj.props;
  const len = (p.length || 1800) * MM;
  const w = (p.width || 700) * MM;
  const h = (p.height || 1300) * MM;
  const shelves = p.shelves || 3;
  const els = [];
  // Body / cabinet
  els.push(<mesh key="body" position={[0, h / 2, 0]} castShadow>
    <boxGeometry args={[len, h, w]} />
    <meshStandardMaterial color="#f8fafc" metalness={0.25} roughness={0.55} />
  </mesh>);
  // Curved-look front glass (approximated by a slightly tilted transparent panel)
  els.push(<mesh key="glass" position={[0, h * 0.6, w / 2 - 0.02]} rotation={[Math.PI * 0.06, 0, 0]}>
    <boxGeometry args={[len - 0.06, h * 0.55, 0.02]} />
    <meshStandardMaterial color={obj.color} transparent opacity={0.35} metalness={0.6} roughness={0.15} />
  </mesh>);
  // Interior shelves
  for (let s = 0; s < shelves; s++) {
    const y = h * 0.3 + (h * 0.5) * (s + 0.5) / shelves;
    els.push(<mesh key={`sh-${s}`} position={[0, y, 0]}><boxGeometry args={[len - 0.12, 0.02, w * 0.7]} /><meshStandardMaterial color="#e2e8f0" /></mesh>);
  }
  // Base
  els.push(<mesh key="base" position={[0, 0.1, 0]}><boxGeometry args={[len, 0.2, w]} /><meshStandardMaterial color="#334155" /></mesh>);
  return <group>{els}</group>;
}

function SignageGate3D({ obj }) {
  const p = obj.props;
  const len = (p.length || 1500) * MM;
  const w = (p.width || 200) * MM;
  const h = (p.height || 1200) * MM;
  const els = [];
  // Two side posts
  els.push(<mesh key="p1" position={[-len / 2, h / 2, 0]}><boxGeometry args={[w, h, w]} /><meshStandardMaterial color="#111827" metalness={0.3} /></mesh>);
  els.push(<mesh key="p2" position={[len / 2, h / 2, 0]}><boxGeometry args={[w, h, w]} /><meshStandardMaterial color="#111827" metalness={0.3} /></mesh>);
  // Top signage bar
  els.push(<mesh key="bar" position={[0, h + 0.06, 0]}><boxGeometry args={[len + w, 0.18, w]} /><meshStandardMaterial color={obj.color} emissive={obj.color} emissiveIntensity={0.3} /></mesh>);
  return <group>{els}</group>;
}

function ObjectMesh({ obj, isSel, onSelect }) {
  // Position object so its CENTER is at obj.position + half(footprint)
  const fp = objectFootprint(obj);
  const cx = (obj.position.x + fp.length / 2) * MM;
  const cz = (obj.position.y + fp.width / 2) * MM;
  // Legacy scalar rotation → interpret as Y.
  const rot = typeof obj.rotation === "number"
    ? { x: 0, y: obj.rotation, z: 0 }
    : { x: 0, y: 0, z: 0, ...(obj.rotation || {}) };
  const rx = (rot.x * Math.PI) / 180;
  const ry = -(rot.y * Math.PI) / 180;
  const rz = (rot.z * Math.PI) / 180;

  let mesh = null;
  switch (obj.type) {
    case "pallet_rack": mesh = <PalletRack3D obj={obj} />; break;
    case "slotted_angle_rack": mesh = <SlottedAngleRack3D obj={obj} />; break;
    case "drive_in_rack": mesh = <DriveInRack3D obj={obj} />; break;
    case "cantilever_rack": mesh = <CantileverRack3D obj={obj} />; break;
    case "mezzanine": mesh = <Mezzanine3D obj={obj} />; break;
    case "forklift": mesh = <Forklift3D obj={obj} />; break;
    case "reach_truck": mesh = <Forklift3D obj={obj} />; break;
    case "vna_truck": mesh = <Forklift3D obj={obj} />; break;
    case "order_picker": mesh = <Forklift3D obj={obj} />; break;
    case "walkie_stacker": mesh = <HandPalletTruck3D obj={obj} />; break;
    case "tow_tractor": mesh = <Forklift3D obj={obj} />; break;
    case "scissor_lift": mesh = <Box3D length={fp.length * MM} width={fp.width * MM} height={(obj.props.height || 2000) * MM} color={obj.color} />; break;
    case "roller_conveyor": mesh = <Box3D length={fp.length * MM} width={fp.width * MM} height={(obj.props.height || 700) * MM} color={obj.color} />; break;
    case "hand_pallet_truck": mesh = <HandPalletTruck3D obj={obj} />; break;
    case "pallet": mesh = <Pallet3D obj={obj} />; break;
    case "wall_rack": mesh = <WallRack3D obj={obj} />; break;
    case "gondola_rack": mesh = <GondolaRack3D obj={obj} />; break;
    case "corner_rack": mesh = <CornerRack3D obj={obj} />; break;
    case "pegboard_display": mesh = <PegboardDisplay3D obj={obj} />; break;
    case "checkout_counter": mesh = <CheckoutCounter3D obj={obj} />; break;
    case "open_chiller": mesh = <OpenChiller3D obj={obj} />; break;
    case "glass_freezer": mesh = <GlassFreezer3D obj={obj} />; break;
    case "chest_freezer": mesh = <ChestFreezer3D obj={obj} />; break;
    case "shopping_trolley": mesh = <ShoppingTrolley3D obj={obj} />; break;
    case "promo_bin": mesh = <PromoBin3D obj={obj} />; break;
    case "bakery_display": mesh = <BakeryDisplay3D obj={obj} />; break;
    case "signage_gate": mesh = <SignageGate3D obj={obj} />; break;
    case "pillar": mesh = <Box3D length={fp.length * MM} width={fp.width * MM} height={(obj.props.height || 6000) * MM} color={obj.color} />; break;
    case "office_block": mesh = <Box3D length={fp.length * MM} width={fp.width * MM} height={(obj.props.height || 3000) * MM} color={obj.color} />; break;
    case "loading_dock": mesh = <Box3D length={fp.length * MM} width={fp.width * MM} height={(obj.props.height || 200) * MM} color={obj.color} />; break;
    default: mesh = <Box3D length={fp.length * MM} width={fp.width * MM} height={1} color={obj.color} />;
  }

  return (
    <group position={[cx, 0, cz]} rotation={[rx, ry, rz]} onClick={(e) => { e.stopPropagation(); onSelect(obj.id); }}>
      {mesh}
      {isSel && (
        <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[Math.max(fp.length, fp.width) * MM * 0.55, Math.max(fp.length, fp.width) * MM * 0.6, 32]} />
          <meshBasicMaterial color="#38bdf8" />
        </mesh>
      )}
    </group>
  );
}

function Obstacle3D({ ob }) {
  const fp = { length: ob.length * MM, width: ob.width * MM };
  const h = (ob.height || 3000) * MM;
  const cx = (ob.x + ob.length / 2) * MM;
  const cz = (ob.y + ob.width / 2) * MM;
  return (
    <mesh position={[cx, h / 2, cz]}>
      <boxGeometry args={[fp.length, h, fp.width]} />
      <meshStandardMaterial color="#444" roughness={0.9} />
    </mesh>
  );
}

// --- Sky/Weather presets — controls Sky component params, fog colour, and lights.
const SKY_PRESETS = {
  day:      { sky: { sunPosition: [100, 30, 100],  turbidity: 6,  rayleigh: 0.8, mieCoefficient: 0.005, mieDirectionalG: 0.7 }, fog: "#dbeafe", bg: "#dbeafe", sunColor: "#fff5e0", sunIntensity: 1.5, hemi: ["#ffffff", "#c9b98d", 0.7], ambient: 0.45, fill: "#cfe0ff", fillIntensity: 0.35, stars: false },
  sunset:   { sky: { sunPosition: [80, 8, 30],     turbidity: 10, rayleigh: 3,   mieCoefficient: 0.02,  mieDirectionalG: 0.85 }, fog: "#fdba74", bg: "#fdba74", sunColor: "#ff9a55", sunIntensity: 1.3, hemi: ["#ff9a55", "#3a2818", 0.55], ambient: 0.35, fill: "#8a7bb0", fillIntensity: 0.25, stars: false },
  overcast: { sky: { sunPosition: [50, 40, 50],    turbidity: 20, rayleigh: 0.2, mieCoefficient: 0.005, mieDirectionalG: 0.7 }, fog: "#94a3b8", bg: "#94a3b8", sunColor: "#e2e8f0", sunIntensity: 0.7, hemi: ["#e2e8f0", "#94a3b8", 0.9],  ambient: 0.7,  fill: "#94a3b8", fillIntensity: 0.4,  stars: false },
  night:    { sky: null,                                                                                                          fog: "#070a14", bg: "#070a14", sunColor: "#8aa0ff", sunIntensity: 0.35,hemi: ["#3b4b7a", "#0b1220", 0.4],  ambient: 0.18, fill: "#4a5b9a", fillIntensity: 0.2,  stars: true  },
};

// --- Procedural ground textures generated once and cached.
function useGroundTexture(kind) {
  return useMemo(() => {
    if (kind === "plain") return null;
    const size = 256;
    const canvas = document.createElement("canvas");
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (kind === "grass") {
      // Base green, then dense random flecks in slightly different greens
      ctx.fillStyle = "#4d7c3a";
      ctx.fillRect(0, 0, size, size);
      for (let n = 0; n < 6000; n++) {
        const x = Math.random() * size;
        const y = Math.random() * size;
        const v = Math.floor(Math.random() * 40) - 20;
        const r = 60 + v, g = 110 + v, b = 40 + Math.floor(v / 2);
        ctx.fillStyle = `rgba(${r},${g},${b},0.75)`;
        ctx.fillRect(x, y, 1.5 + Math.random() * 1.5, 1.5 + Math.random() * 1.5);
      }
    } else if (kind === "asphalt") {
      // Base dark gray, sparse speckle
      ctx.fillStyle = "#3b3b3b";
      ctx.fillRect(0, 0, size, size);
      for (let n = 0; n < 3500; n++) {
        const x = Math.random() * size;
        const y = Math.random() * size;
        const v = Math.floor(Math.random() * 60) - 25;
        const grey = 80 + v;
        ctx.fillStyle = `rgba(${grey},${grey},${grey},0.6)`;
        ctx.fillRect(x, y, 1 + Math.random(), 1 + Math.random());
      }
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(40, 40);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    return tex;
  }, [kind]);
}

/**
 * Procedural finish textures for interior walls & floor.
 * kinds: 'plain' (returns null — pure colour), 'brick', 'tile', 'wood',
 * 'epoxy', 'concrete'. `tone` is a base colour hex the pattern is drawn over.
 *
 * Returns an object { map, roughness, repeat } — the material consumer scales
 * `repeat` by the mesh size (m) so patterns look physically-correct.
 */
function useFinishTexture(kind, tone) {
  return useMemo(() => {
    if (!kind || kind === "plain") return null;
    const size = 512;
    const canvas = document.createElement("canvas");
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext("2d");
    // Base fill
    ctx.fillStyle = tone || "#c8c2b6";
    ctx.fillRect(0, 0, size, size);

    if (kind === "brick") {
      // Terracotta bricks, offset rows, dark mortar lines.
      const brickW = 128, brickH = 48, mortar = 4;
      ctx.fillStyle = "#3a2a20";
      ctx.fillRect(0, 0, size, size);
      for (let row = 0; row < size / brickH + 2; row++) {
        const offset = (row % 2) * (brickW / 2);
        for (let col = -1; col < size / brickW + 2; col++) {
          const x = col * brickW - offset;
          const y = row * brickH;
          // Slight brick colour variance
          const v = Math.floor(Math.random() * 40) - 20;
          const r = 178 + v, g = 90 + Math.floor(v / 2), b = 65 + Math.floor(v / 3);
          ctx.fillStyle = `rgb(${r},${g},${b})`;
          ctx.fillRect(x + mortar / 2, y + mortar / 2, brickW - mortar, brickH - mortar);
          // Speckle
          for (let n = 0; n < 12; n++) {
            const sx = x + mortar / 2 + Math.random() * (brickW - mortar);
            const sy = y + mortar / 2 + Math.random() * (brickH - mortar);
            ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.15})`;
            ctx.fillRect(sx, sy, 1.5, 1.5);
          }
        }
      }
      return { canvas, repeat: [4, 4], roughness: 0.9 };
    }

    if (kind === "tile") {
      // Glossy ceramic tiles with grout lines. Tone tints the tile face.
      const tile = 64, grout = 3;
      ctx.fillStyle = "#d5d5d5";
      ctx.fillRect(0, 0, size, size);
      for (let y = 0; y < size; y += tile) {
        for (let x = 0; x < size; x += tile) {
          // Slight tile-to-tile variance
          const v = Math.floor(Math.random() * 20) - 10;
          const base = tone || "#e5e5e5";
          ctx.fillStyle = shadeHex(base, v);
          ctx.fillRect(x + grout / 2, y + grout / 2, tile - grout, tile - grout);
          // Subtle sheen
          ctx.fillStyle = "rgba(255,255,255,0.18)";
          ctx.fillRect(x + grout / 2, y + grout / 2, tile - grout, (tile - grout) / 4);
        }
      }
      return { canvas, repeat: [8, 8], roughness: 0.25 };
    }

    if (kind === "wood") {
      // Vertical wood plank flooring with grain.
      const plank = 96;
      const baseTone = tone || "#a0673a";
      for (let x = 0; x < size; x += plank) {
        // Alternate plank shading
        ctx.fillStyle = shadeHex(baseTone, x % (plank * 2) === 0 ? 0 : -18);
        ctx.fillRect(x, 0, plank, size);
        // Grain lines
        for (let n = 0; n < 60; n++) {
          const y = Math.random() * size;
          const w = 0.4 + Math.random() * 1.3;
          ctx.strokeStyle = `rgba(60,30,15,${0.1 + Math.random() * 0.25})`;
          ctx.lineWidth = w;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.bezierCurveTo(
            x + plank * 0.3, y + (Math.random() - 0.5) * 10,
            x + plank * 0.7, y + (Math.random() - 0.5) * 10,
            x + plank, y + (Math.random() - 0.5) * 6
          );
          ctx.stroke();
        }
        // Plank seam (dark)
        ctx.fillStyle = "rgba(0,0,0,0.4)";
        ctx.fillRect(x, 0, 1, size);
        // Knots
        if (Math.random() < 0.6) {
          const ky = Math.random() * size;
          const kr = 3 + Math.random() * 4;
          const grd = ctx.createRadialGradient(x + plank / 2, ky, 0, x + plank / 2, ky, kr);
          grd.addColorStop(0, "rgba(50,20,10,0.6)");
          grd.addColorStop(1, "rgba(50,20,10,0)");
          ctx.fillStyle = grd;
          ctx.beginPath();
          ctx.arc(x + plank / 2, ky, kr, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      return { canvas, repeat: [4, 4], roughness: 0.55 };
    }

    if (kind === "epoxy") {
      // Glossy poured-epoxy floor — subtle swirl + high shine.
      ctx.fillStyle = tone || "#4a6a7a";
      ctx.fillRect(0, 0, size, size);
      // Swirls
      for (let n = 0; n < 40; n++) {
        const grd = ctx.createRadialGradient(
          Math.random() * size, Math.random() * size, 0,
          Math.random() * size, Math.random() * size, 60 + Math.random() * 120
        );
        grd.addColorStop(0, `rgba(255,255,255,${0.03 + Math.random() * 0.05})`);
        grd.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, size, size);
      }
      // Very fine speckle
      for (let n = 0; n < 400; n++) {
        ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.08})`;
        ctx.fillRect(Math.random() * size, Math.random() * size, 1, 1);
      }
      return { canvas, repeat: [2, 2], roughness: 0.08 };
    }

    if (kind === "concrete") {
      // Polished concrete — subtle mottled greys over the base tone.
      ctx.fillStyle = tone || "#c8c2b6";
      ctx.fillRect(0, 0, size, size);
      for (let n = 0; n < 800; n++) {
        const v = Math.floor(Math.random() * 40) - 20;
        ctx.fillStyle = `rgba(${100 + v},${100 + v},${100 + v},${0.05 + Math.random() * 0.08})`;
        const r = 3 + Math.random() * 20;
        ctx.beginPath();
        ctx.arc(Math.random() * size, Math.random() * size, r, 0, Math.PI * 2);
        ctx.fill();
      }
      // Occasional dark speck
      for (let n = 0; n < 250; n++) {
        ctx.fillStyle = `rgba(0,0,0,${0.05 + Math.random() * 0.1})`;
        ctx.fillRect(Math.random() * size, Math.random() * size, 1, 1);
      }
      return { canvas, repeat: [3, 3], roughness: 0.6 };
    }

    return null;
  }, [kind, tone]);
}

// Utility used by finish textures — shift a #rrggbb hex by ±v on each channel.
function shadeHex(hex, delta) {
  const h = (hex || "#888888").replace("#", "");
  const r = Math.max(0, Math.min(255, parseInt(h.substr(0, 2), 16) + delta));
  const g = Math.max(0, Math.min(255, parseInt(h.substr(2, 2), 16) + delta));
  const b = Math.max(0, Math.min(255, parseInt(h.substr(4, 2), 16) + delta));
  return `rgb(${r},${g},${b})`;
}

/**
 * Wrap the raw `useFinishTexture` canvas into a THREE.CanvasTexture set up for
 * repeating on a mesh of given world dimensions (mm). Returns a stable texture
 * cached on the returned object.
 */
function useMaterialFinish(finish, color, worldDims) {
  const base = useFinishTexture(finish, color);
  return useMemo(() => {
    if (!base) return { map: null, roughness: 0.7, color: color || "#c8c2b6" };
    const tex = new THREE.CanvasTexture(base.canvas);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.colorSpace = THREE.SRGBColorSpace;
    // Scale repeat so a tile / brick unit is roughly a real-world size.
    // worldDims is [wm, hm] in metres — one texture tile ≈ 2m by default.
    if (worldDims) {
      const [wm, hm] = worldDims;
      tex.repeat.set(Math.max(1, wm / 2) * base.repeat[0] / 4, Math.max(1, hm / 2) * base.repeat[1] / 4);
    } else {
      tex.repeat.set(base.repeat[0], base.repeat[1]);
    }
    tex.needsUpdate = true;
    return { map: tex, roughness: base.roughness, color: "#ffffff" };
  }, [base, color, worldDims]);
}

// Small helper that resets camera & OrbitControls target when fitVersion changes.
function CameraFit({ fitVersion, target, camPos }) {
  const { camera, controls } = useThree();
  React.useEffect(() => {
    if (fitVersion === 0) return;
    camera.position.set(camPos[0], camPos[1], camPos[2]);
    camera.lookAt(target[0], target[1], target[2]);
    camera.updateProjectionMatrix();
    if (controls) {
      controls.target.set(target[0], target[1], target[2]);
      controls.update();
    }
  }, [fitVersion, camera, controls, camPos, target]);
  return null;
}

/**
 * SnapshotBridge — installs a global function that renders the 3D scene from
 * an arbitrary camera position/target at an arbitrary resolution and returns
 * a PNG data URL. Used by the PDF exporter so the report contains proper
 * "camera views" (isometric / front / side / top-down) instead of a raw
 * on-screen screenshot with UI overlays.
 */
function SnapshotBridge() {
  const { gl, scene, camera } = useThree();
  useEffect(() => {
    /**
     * Snapshot the scene. opts = {
     *   view: 'iso' | 'iso2' | 'front' | 'side' | 'top' | null
     *   bounds: { center:{x,y,z}, size:{x,y,z} } — required when `view` is set.
     * }
     */
    window.__EDITOR_3D_SNAPSHOT__ = (opts = {}) => {
      const { view = null, bounds = null } = opts;
      const canvas = gl.domElement;
      // Force a 16:9 landscape render at 1800×1013 so all four PDF pages have
      // consistent width and nothing at the edges is clipped by portrait /
      // narrow on-screen aspect ratios. Restored in `finally`.
      const SNAP_W = 1800, SNAP_H = 1013;
      const prevSize = { w: canvas.width, h: canvas.height };
      const prevPixelRatio = gl.getPixelRatio ? gl.getPixelRatio() : 1;
      const prevCam = {
        pos: camera.position.clone(),
        quat: camera.quaternion.clone(),
        near: camera.near,
        far: camera.far,
        up: camera.up.clone(),
        aspect: camera.aspect,
      };
      const hiddenWalls = [];
      try {
        if (gl.setPixelRatio) gl.setPixelRatio(1);
        gl.setSize(SNAP_W, SNAP_H, false);
        camera.aspect = SNAP_W / SNAP_H;
        // For perspective corner views, temporarily hide the warehouse walls
        // so the interior racks / MHE are clearly visible in the report.
        // Walls are tagged with `userData.isWall`. Top-down keeps them so the
        // polygon rim reads as a clear boundary.
        if (view && view !== "top") {
          scene.traverse((obj) => {
            if (obj.isMesh && obj.userData && obj.userData.isWall) {
              hiddenWalls.push({ obj, prev: obj.visible });
              obj.visible = false;
            }
          });
        }
        // Optionally frame the scene from a named angle. Callers pass explicit
        // `bounds = { center, size }` (in world metres) because scene-based
        // bbox computation is unreliable (Sky/Environment inflate it).
        if (view && bounds) {
          const center = new THREE.Vector3(bounds.center.x, bounds.center.y, bounds.center.z);
          const sz     = new THREE.Vector3(bounds.size.x,   bounds.size.y,   bounds.size.z);
          // Include Y so tall scenes aren't cropped, and add extra padding so
          // walls / doors near the edge aren't cut off at the frame boundary.
          const diag = Math.sqrt(sz.x * sz.x + sz.z * sz.z);
          const fovRad = (camera.fov * Math.PI) / 180;
          // Camera FOV in three.js is VERTICAL. For a widescreen (16:9) frame
          // the horizontal FOV is wider, so the camera can sit CLOSER — hence
          // divide by max(1, aspect), not min. This gives a true zoom-fit
          // shot instead of a distant "warehouse-in-a-sea-of-sky" render.
          const aspect = camera.aspect || 1;
          const distFit = (diag / 2) / Math.tan(fovRad / 2) / Math.max(1, aspect);
          // Just 8 % padding — tight zoom-fit as the user requested.
          const dist = distFit * 1.08;
          const eyeH = center.y + Math.max(sz.y * 0.55, 2);
          let camPos;
          // Reset up-vector for perspective views (may have been changed by the
          // previous top-down snapshot).
          camera.up.set(0, 1, 0);
          switch (view) {
            // Perspective corners — SE, NE, NW, SW. All look inward and down.
            case "ne":    camPos = new THREE.Vector3(center.x + dist * 0.72, eyeH + dist * 0.38, center.z - dist * 0.72); break;
            case "nw":    camPos = new THREE.Vector3(center.x - dist * 0.72, eyeH + dist * 0.38, center.z - dist * 0.72); break;
            case "sw":    camPos = new THREE.Vector3(center.x - dist * 0.72, eyeH + dist * 0.38, center.z + dist * 0.72); break;
            case "top":
              // Look straight down. Set camera.up = (0,0,-1) so +X points right
              // and +Z points UP the page — this keeps the floor plan axis-
              // aligned with the paper (no arbitrary roll from lookAt).
              camPos = new THREE.Vector3(center.x, center.y + dist * 1.15, center.z);
              camera.up.set(0, 0, -1);
              break;
            case "iso":
            case "se":
            default:      camPos = new THREE.Vector3(center.x + dist * 0.72, eyeH + dist * 0.38, center.z + dist * 0.72); break;
          }
          camera.position.copy(camPos);
          camera.lookAt(center);
          camera.near = Math.max(0.1, dist * 0.02);
          camera.far  = dist * 8;
          camera.updateMatrixWorld(true);
          camera.updateProjectionMatrix();
        }
        gl.render(scene, camera);
        return canvas.toDataURL("image/png");
      } catch (e) {
        console.error("3D snapshot failed", e);
        return null;
      } finally {
        // Restore any temporarily hidden walls first (so the on-screen editor
        // renders correctly on the next frame).
        for (const h of hiddenWalls) h.obj.visible = h.prev;
        camera.position.copy(prevCam.pos);
        camera.quaternion.copy(prevCam.quat);
        camera.up.copy(prevCam.up);
        camera.near = prevCam.near;
        camera.far  = prevCam.far;
        camera.aspect = prevCam.aspect;
        camera.updateProjectionMatrix();
        // Restore on-screen renderer size so the editor viewport keeps working.
        if (gl.setPixelRatio) gl.setPixelRatio(prevPixelRatio);
        gl.setSize(prevSize.w, prevSize.h, false);
        gl.render(scene, camera);
      }
    };
    return () => { delete window.__EDITOR_3D_SNAPSHOT__; };
  }, [gl, scene, camera]);
  return null;
}

export default function Canvas3D() {
  const floor = useEditor((s) => s.floor);
  const objects = useEditor((s) => s.objects);
  const selectedId = useEditor((s) => s.selectedId);
  const setSelected = useEditor((s) => s.setSelected);
  const showGrid = useEditor((s) => s.showGrid);
  const handMode = useEditor((s) => s.handMode);
  const fitVersion = useEditor((s) => s.fitVersion);
  const skyPreset = useEditor((s) => s.skyPreset);
  const groundStyle = useEditor((s) => s.groundStyle);
  const preset = SKY_PRESETS[skyPreset] || SKY_PRESETS.day;
  const groundTex = useGroundTexture(groundStyle);

  // compute building bbox center for camera target
  const verts = floor.vertices || [];
  const xs = verts.map((v) => v.x), ys = verts.map((v) => v.y);
  const minX = xs.length ? Math.min(...xs) : 0, maxX = xs.length ? Math.max(...xs) : 30000;
  const minY = ys.length ? Math.min(...ys) : 0, maxY = ys.length ? Math.max(...ys) : 20000;
  const cx = ((minX + maxX) / 2) * MM;
  const cz = ((minY + maxY) / 2) * MM;
  const span = Math.max(maxX - minX, maxY - minY) * MM;
  const bboxL = (maxX - minX) * MM;
  const bboxW = (maxY - minY) * MM;

  // Base ground colour when no texture is applied (plain), varies by sky mood.
  const plainGroundColor = skyPreset === "night" ? "#1e293b"
    : skyPreset === "sunset" ? "#8a6b52"
    : skyPreset === "overcast" ? "#6b7280"
    : "#7a8471";

  return (
    <div className="w-full h-full relative" style={{ background: preset.bg }}>
      <Canvas
        shadows
        gl={{ preserveDrawingBuffer: true, antialias: true }}
        camera={{ position: [cx + span * 0.7, span * 0.6, cz + span * 0.9], fov: 45, near: 0.1, far: 1000 }}
        onPointerMissed={() => setSelected(null)}
      >
        <SnapshotBridge />
        {/* Sky — a procedural sky-dome for daytime moods; night falls back to a dark background + stars */}
        {preset.sky ? (
          <Sky
            sunPosition={preset.sky.sunPosition}
            turbidity={preset.sky.turbidity}
            rayleigh={preset.sky.rayleigh}
            mieCoefficient={preset.sky.mieCoefficient}
            mieDirectionalG={preset.sky.mieDirectionalG}
          />
        ) : (
          <color attach="background" args={[preset.bg]} />
        )}
        {preset.stars && <Stars radius={100} depth={50} count={5000} factor={4} fade speed={1} />}
        <fog attach="fog" args={[preset.fog, span * 3, span * 8]} />

        {/* Ambient + directional sun + subtle fill — all driven by the active preset */}
        <hemisphereLight args={[preset.hemi[0], preset.hemi[1], preset.hemi[2]]} />
        <directionalLight
          position={[span * (preset.sky?.sunPosition?.[0] || 1) / 100 || span, span * 1.4, span]}
          intensity={preset.sunIntensity}
          color={preset.sunColor}
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
          shadow-camera-left={-span * 1.5}
          shadow-camera-right={span * 1.5}
          shadow-camera-top={span * 1.5}
          shadow-camera-bottom={-span * 1.5}
          shadow-bias={-0.0005}
        />
        <directionalLight position={[-span, span * 0.8, -span * 0.4]} intensity={preset.fillIntensity} color={preset.fill} />
        <ambientLight intensity={preset.ambient} />

        {/* Outdoor ground plane surrounding the warehouse.
            Textured (grass / asphalt) when selected, plain moody colour otherwise.
            NO `receiveShadow` — we don't want a huge wall-shadow rectangle stretching
            across the outdoor plane. Only the interior floor / racks receive shadows. */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[cx, -0.02, cz]} userData={{ ignoreBounds: true }}>
          <planeGeometry args={[span * 20, span * 20]} />
          {groundTex ? (
            <meshStandardMaterial map={groundTex} roughness={0.95} />
          ) : (
            <meshStandardMaterial color={plainGroundColor} roughness={0.95} />
          )}
        </mesh>

        {/* IBL for a small amount of environmental reflection */}
        <Environment preset={skyPreset === "night" ? "night" : "warehouse"} />

        {showGrid && (
          <Grid
            args={[bboxL - 0.2, bboxW - 0.2]}
            position={[cx, 0.001, cz]}
            cellSize={1}
            cellThickness={0.5}
            cellColor="#a5a19a"
            sectionSize={5}
            sectionThickness={1.0}
            sectionColor="#4b5563"
            fadeDistance={Math.min(bboxL, bboxW) * 0.55}
            fadeStrength={2}
          />
        )}

        <Suspense fallback={null}>
          <Floor floor={floor} />
          {(floor.obstacles || []).map((ob) => <Obstacle3D key={ob.id} ob={ob} />)}
          {objects.filter((o) => !o.hidden).map((o) => (
            <ObjectMesh key={o.id} obj={o} isSel={o.id === selectedId} onSelect={setSelected} />
          ))}
        </Suspense>

        <OrbitControls
          target={[cx, 0, cz]}
          makeDefault
          enableDamping
          mouseButtons={handMode
            ? { LEFT: THREE.MOUSE.PAN,    MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.ROTATE }
            : { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN }}
        />
        <CameraFit
          fitVersion={fitVersion}
          target={[cx, 0, cz]}
          camPos={[cx + span * 0.7, span * 0.6, cz + span * 0.9]}
        />
      </Canvas>
      <div className="absolute top-2 left-2 bg-white/85 backdrop-blur-sm border border-slate-200 rounded-sm px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-700 pointer-events-none shadow-sm">
        3D · {skyPreset} · {groundStyle} · scroll = zoom
      </div>
    </div>
  );
}
